// src/lib/queryClient.ts
import { QueryClient, MutationCache, QueryCache } from "@tanstack/react-query";
import notificationStore from "./Notification/notificationStore";

const getServerMessage = (data: any): string | undefined =>
  data?.message || data?.msg || undefined; // adapt to your API

/**
 * The text a REJECTED request carried — a Nest exception's `{ statusCode,
 * message }` body. Axios puts none of it on the Error, whose `message` is only
 * "Request failed with status code 409", so without this every server-side
 * refusal reached the user as a bare status number: the scheduler's
 * "Therapeut hat kein Heilmittel für diese Therapie: KG-ZNS" was invisible.
 */
const getServerErrorMessage = (error: any): string | undefined => {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  // class-validator rejections arrive as an array of constraint failures
  if (Array.isArray(message) && message.length) return message.join(", ");
  return undefined;
};
// Requests aborted client-side (e.g. no auth token yet) are not real failures.
const isCancelled = (error: unknown) => (error as any)?.code === "ERR_CANCELED";

/**
 * A 409 is the API's way of saying "this is not allowed", not "something
 * broke": an uncredentialed therapist, an occupied slot, a rule the request
 * violated. The user is being warned about their own action — the same thing
 * the client-side gates report as a `warning` — so it is presented that way
 * rather than in the alarming red of a genuine failure.
 */
const severityOf = (error: any): "warning" | "error" =>
  (error?.status ?? error?.response?.status) === 409 ? "warning" : "error";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const meta = query.meta as any;
      if (meta?.silenceErrors || isCancelled(error)) return;
      const msg =
        (error as any)?.message || meta?.errorMessage || "Fehler beim Laden";
      notificationStore.showMessage(msg, "error", error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      const meta = mutation.meta as any;
      if (meta?.silenceErrors || isCancelled(error)) return;
      const msg =
        getServerErrorMessage(error) ||
        (error as any)?.message ||
        meta?.errorMessage ||
        "Aktion fehlgeschlagen";
      notificationStore.showMessage(msg, severityOf(error), error);
    },
    onSuccess: (data, _vars, _ctx, mutation) => {
      const meta = mutation.meta as any;
      const msg = meta?.successMessage ?? getServerMessage(data);
      if (msg && !meta?.silenceSuccess) {
        notificationStore.showMessage(msg, "success");
      }
    },
  }),

  // The react query defaults are fine, but here we could make our own global defaults if needed:
  // defaultOptions: {
  //   queries: {
  //     retry: 1,
  //     refetchOnWindowFocus: false,
  //     staleTime: 60_000,
  //   },
  //   mutations: {
  //     retry: 0,
  //   },
  // },
});

// Utility functions for manipulating lists by ID
export type IdLike = string | number;
export type WithId<T extends IdLike = IdLike> = { id: T };

export const upsertById =
  <T extends WithId>(item: T) =>
  (list: T[] = []) => {
    const i = list.findIndex((x) => x.id === item.id);
    if (i === -1) return [item, ...list];
    const copy = list.slice();
    copy[i] = { ...copy[i], ...item };
    return copy;
  };

export const removeById =
  <T extends WithId>(id: T["id"]) =>
  (list: T[] = []) =>
    list.filter((x) => x.id !== id);
